import { useParams } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import ProjectLayout from '@/components/ProjectLayout'
import { projectService, projectLabel, type Project } from '@/services/projectService'
import { HttpError } from '@/lib/httpError'
import { ApiError } from '@/lib/apiError'
import LoadingOverlay from '@/components/ui/LoadingOverlay'
import ErrorDisplay from '@/components/ui/ErrorDisplay'
import DeleteProjectModal, { type DeleteProjectModalRef } from '@/components/modals/DeleteProjectModal'
import { dateLocaleFor } from '@/lib/dateLocale'

export default function ProjectInformationPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const { t, i18n } = useTranslation('projectInformation')
  const { refreshAuth, user } = useAuth()
  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const deleteModalRef = useRef<DeleteProjectModalRef>(null)

  // Editing the project's details, which means its display name and its
  // description. Not its namespace: see the note on the Namespace field.
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState({ display_name: '', description: '' })
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  // Permission checks for delete
  // One of our own accounts, not the platform role: every client admin is a
  // platform owner, so the role would hand each client the others' projects.
  const isSuperuser = user?.superuser === true
  const isProjectOwner = project?.role === 'owner'
  const isProjectAdmin = project?.role === 'admin'
  
  // Direct delete: platform owner or project owner
  const canDirectDelete = isSuperuser || isProjectOwner
  // Request delete: platform owner or project owner or project admin
  const canRequestDelete = isSuperuser || isProjectOwner || isProjectAdmin
  // Show delete button
  const canDelete = canDirectDelete || canRequestDelete
  // Same reach as a direct delete, which is what the backend enforces.
  const canEditDetails = canDirectDelete

  const fetchProjectInfo = async () => {
    if (!projectId) return

    setLoading(true)
    setError('')

    try {
      const data = await projectService.getProjectById(projectId)
      setProject(data)
    } catch (err) {
      // Handle 401 errors with automatic token refresh
      if (err instanceof HttpError && err.status === 401) {
        try {
          await refreshAuth()
          // Retry the fetch after token refresh
          const data = await projectService.getProjectById(projectId)
          setProject(data)
        } catch (refreshError) {
          // If refresh fails, show error
          setError('Failed to load project information')
        } finally {
          setLoading(false)
        }
        return
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to load project information'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProjectInfo()
  }, [projectId])

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      const locale = dateLocaleFor(i18n.language)

      return date.toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
    } catch {
      return dateString
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200'
      case 'archived':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getRoleColor = (role?: string) => {
    switch (role?.toLowerCase()) {
      case 'owner':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'admin':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'member':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'viewer':
        return 'bg-gray-100 text-gray-800 border-gray-200'
      case 'observer':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  if (loading) {
    return (
      <ProjectLayout>
        <LoadingOverlay message={t('loading', 'Loading project information...')} fullScreen />
      </ProjectLayout>
    )
  }

  if (error) {
    return (
      <ProjectLayout>
        <ErrorDisplay
          error={error}
          title={t('errors.title', 'Error')}
          onRefresh={fetchProjectInfo}
          fullScreen
        />
      </ProjectLayout>
    )
  }

  if (!project) {
    return (
      <ProjectLayout>
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-gray-500">{t('notFound', 'Project not found')}</p>
          </div>
        </div>
      </ProjectLayout>
    )
  }

  const startEditing = () => {
    if (!project) return
    setDraft({ display_name: project.display_name ?? '', description: project.description ?? '' })
    setSaveError('')
    setEditing(true)
  }

  const cancelEditing = () => {
    setEditing(false)
    setSaveError('')
  }

  const saveDetails = async () => {
    if (!projectId || saving) return

    setSaving(true)
    setSaveError('')

    try {
      const updated = await projectService.updateProjectDetails(projectId, {
        display_name: draft.display_name.trim(),
        description: draft.description.trim(),
      })
      // The response omits the membership role, which only the list endpoint
      // fills in; keep the one already loaded so the page does not lose it.
      setProject(previous => (previous ? { ...previous, ...updated, role: previous.role } : updated))
      setEditing(false)
    } catch (err) {
      if (err instanceof ApiError) {
        setSaveError(t(err.message, { defaultValue: err.detail || err.message }))
      } else {
        setSaveError(t('errors.updateFailed', 'Could not save the changes'))
      }
    } finally {
      setSaving(false)
    }
  }

  const handleOpenDeleteModal = () => {
    if (project) {
      deleteModalRef.current?.open(project)
    }
  }

  return (
    <ProjectLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-text-dark">{t('title', 'Project Information')}</h1>
            <p className="text-gray-600 mt-2">{t('description', 'View and manage project details')}</p>
          </div>
          {canDelete && (
            <button
              onClick={handleOpenDeleteModal}
              className="flex items-center gap-2 px-4 py-2 ml-4 mt-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span>{t('deleteProject', 'Delete Project')}</span>
            </button>
          )}
        </div>

        <div className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
              <h2 className="text-lg font-semibold text-gray-900">{t('sections.basic', 'Basic Information')}</h2>
              {canEditDetails && !editing && (
                <button
                  onClick={startEditing}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-primary border border-primary/30 rounded-lg hover:bg-primary/5 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  {t('actions.edit', 'Edit')}
                </button>
              )}
              {editing && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={cancelEditing}
                    disabled={saving}
                    className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {t('actions.cancel', 'Cancel')}
                  </button>
                  <button
                    onClick={saveDetails}
                    disabled={saving}
                    className="px-3 py-1.5 text-sm text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
                  >
                    {saving ? t('actions.saving', 'Saving...') : t('actions.save', 'Save')}
                  </button>
                </div>
              )}
            </div>
            <div className="p-6">
              {saveError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {saveError}
                </div>
              )}
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.name', 'Project Name')}</dt>
                  {editing ? (
                    <dd>
                      <input
                        type="text"
                        value={draft.display_name}
                        onChange={e => setDraft(prev => ({ ...prev, display_name: e.target.value }))}
                        maxLength={255}
                        placeholder={project.name}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {t('hints.displayName', 'Leave this blank to show the namespace instead.')}
                      </p>
                    </dd>
                  ) : (
                    <dd className="text-base text-gray-900">{projectLabel(project)}</dd>
                  )}
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.namespace', 'Namespace')}</dt>
                  <dd className="text-base text-gray-900 font-mono text-sm">{project.name}</dd>
                  {editing && (
                    <p className="text-xs text-gray-500 mt-1">
                      {t('hints.namespace', 'Fixed when the project was created. The cluster addresses the project by this name.')}
                    </p>
                  )}
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.id', 'Project ID')}</dt>
                  <dd className="text-base text-gray-900 font-mono text-sm">{project.id}</dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.description', 'Description')}</dt>
                  {editing ? (
                    <dd>
                      <textarea
                        value={draft.description}
                        onChange={e => setDraft(prev => ({ ...prev, description: e.target.value }))}
                        maxLength={2000}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-y"
                      />
                    </dd>
                  ) : (
                    <dd className="text-base text-gray-900">{project.description || '-'}</dd>
                  )}
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.status', 'Status')}</dt>
                  <dd>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(project.status)}`}>
                      {t(`status.${project.status}`, project.status)}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.yourRole', 'Your Role')}</dt>
                  <dd>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getRoleColor(project.role)}`}>
                      {t(`roles.${project.role}`, project.role || '-')}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Owner Information */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{t('sections.owner', 'Owner Information')}</h2>
            </div>
            <div className="p-6">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.ownerName', 'Owner Name')}</dt>
                  <dd className="text-base text-gray-900">{project.owner.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.ownerAccount', 'Owner Account')}</dt>
                  <dd className="text-base text-gray-900">{project.owner.account}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.ownerType', 'Account Type')}</dt>
                  <dd className="text-base text-gray-900 capitalize">{project.owner.type}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.platformRole', 'Platform Role')}</dt>
                  <dd className="text-base text-gray-900 capitalize">{project.owner.platform_role}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Timestamps */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{t('sections.timestamps', 'Timestamps')}</h2>
            </div>
            <div className="p-6">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.createdAt', 'Created At')}</dt>
                  <dd className="text-base text-gray-900">{formatDate(project.created_at)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.updatedAt', 'Updated At')}</dt>
                  <dd className="text-base text-gray-900">{formatDate(project.updated_at)}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Project Modal */}
      <DeleteProjectModal
        ref={deleteModalRef}
        canDirectDelete={canDirectDelete}
        canRequestDelete={canRequestDelete}
      />
    </ProjectLayout>
  )
}
